import { z } from "zod";

/**
 * The two tenant settings — the ONLY per-tenant policy switches on the
 * platform.
 *
 * Foundations 1 (Users), B0-T1. The founder abolished feature flags and plans
 * on 2026-09-08 (docs/system/decisions.md, "Features, flags and plans"):
 *
 *   1. **No feature flags.** Every rail and every capability inside a rail is
 *      simply on. The tenant "Features" page, the plan table
 *      (`feature_plan_defaults`), the `feature_flags` catalogue and the 22
 *      toggle keys are removed by migration c22.
 *   2. **No plans.** Free / Starter / Professional / Enterprise are gone.
 *      Deployments are on-prem, one customer per deployment.
 *   3. **Two tenant admin settings**, owned by the tenant admin (not the
 *      sysadmin), stored in `tenant_admin_configs` under namespace `policy`
 *      and read by exactly one resolver,
 *      `packages/api/src/policy/tenant-settings.ts`.
 *
 * This enum is the TYPE source (principles.md #3: the Zod schema declares
 * which keys are legal; nothing else re-types them). There is no catalogue
 * table any more — two booleans do not need one.
 */
export const TenantSettingKeySchema = z.enum([
  /**
   * Allow web search — may this tenant's answers reach the public web?
   * Read by `pipeline/filters/web-search.filter.ts` (the enforcement point)
   * and surfaced by `routes/v1/ecosystem-query.ts`, which refuses a turn that
   * asks for web with `403 { reason: 'web_search_disabled' }`.
   */
  "allow_web_search",
  /**
   * Allow external connections — may this tenant connect out to outside
   * systems at all? This is what "air-gap" meant, moved from platform
   * settings to the tenant's admin settings (decisions.md 2026-09-08 §3):
   * off = no connectors and no outbound MCP; on = the tenant may connect out.
   * Read by `routes/v1/connections-module.ts`, `routes/v1/mcp-servers.ts` and
   * `tools/tool-caller.ts`.
   */
  "allow_external_connections",
]);

export type TenantSettingKey = z.infer<typeof TenantSettingKeySchema>;

/**
 * What `resolveTenantSettings(orgId)` answers: EXHAUSTIVE over
 * `TenantSettingKeySchema.options`. There is no "missing key" state — a key
 * that cannot be resolved is an error, not an implicit allow
 * (principles.md #4, nothing fails open).
 */
export const TenantSettingsSchema = z.object({
  allow_web_search: z.boolean(),
  allow_external_connections: z.boolean(),
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;
